import type { ComponentProps, Dispatch, SetStateAction } from "react";
import { createContext, useContext } from "react";

import { useBoolean } from "@/shared/hooks";
import { Popover } from "@/shared/ui";
import { tw } from "@/shared/utils";

interface PopoverTooltipContextValue {
  showTooltip: boolean;
  closeTooltip: VoidFunction;
  openTooltip: VoidFunction;
  setTooltip: Dispatch<SetStateAction<boolean>>;
}

const PopoverTooltipContext = createContext<PopoverTooltipContextValue | null>(
  null,
);

const usePopoverTooltipContext = () => {
  const context = useContext(PopoverTooltipContext);

  if (!context) {
    throw new Error(
      "usePopoverTooltipContext must be used within a PopoverTooltipProvider",
    );
  }

  return context;
};

const Root = ({ ...props }: ComponentProps<typeof Popover.Root>) => {
  const {
    value: showTooltip,
    setFalse: closeTooltip,
    setTrue: openTooltip,
    setValue: setTooltip,
  } = useBoolean(false);

  return (
    <PopoverTooltipContext.Provider
      value={{
        showTooltip,
        closeTooltip,
        openTooltip,
        setTooltip,
      }}
    >
      <Popover.Root open={showTooltip} onOpenChange={setTooltip} {...props} />
    </PopoverTooltipContext.Provider>
  );
};

const Trigger = ({ ...props }: ComponentProps<typeof Popover.Trigger>) => {
  const { openTooltip, closeTooltip } = usePopoverTooltipContext();

  return (
    <Popover.Trigger
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
      {...props}
    />
  );
};

const Content = ({
  className,
  ...props
}: ComponentProps<typeof Popover.Content>) => {
  return (
    <Popover.Content
      side="top"
      sideOffset={8}
      className={tw("max-w-(--radix-popover-trigger-width)", className)}
      {...props}
    />
  );
};

export const PopoverTooltip = {
  Root,
  Trigger,
  Content,
};
